import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Wifi,
  WifiOff,
  AlertTriangle,
  Trash2,
  ShieldCheck,
  Download,
  Activity,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingPage } from '@/components/ui/loading';
import { Modal } from '@/components/ui/modal';
import { GoogleMap } from '@/components/ui/google-map';
import { usePagePolling, POLL_INTERVAL_DETAIL_MS } from '@/lib/use-page-polling';
import type { MapMarker } from '@/components/ui/google-map';
import { GatewayLocationDialog } from './gateway-location-dialog';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { DeviceStatus, UserRole } from '@/types/api';
import type { Gateway, GatewayCertificate, GatewayDevice, GatewayStatus } from '@/types/api';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import {
  getGateway,
  updateGatewayNotes,
  deleteGateway,
  createGatewayCertificate,
  deleteGatewayCertificate,
  getGatewayDevices,
  getServerTrustCertificate,
} from '../api/gateways-api';

const STALE_MS = 24 * 60 * 60 * 1000;

const statusBadge: Record<GatewayStatus, 'warning' | 'success' | 'default' | 'error'> = {
  Provisioning: 'warning',
  Active: 'success',
  Inactive: 'default',
  Error: 'error',
};

function downloadFile(filename: string, content: string) {
  const blob = new Blob([content], { type: 'application/x-pem-file' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function GatewayDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const activeRole = useActiveRole();
  // Only system administrators (Owner) can provision, manage certificates, or delete a gateway.
  const isSystemAdmin = activeRole === UserRole.Owner;
  // Any role except Viewer can tag / retag a gateway's physical location.
  const canEditLocation =
    activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;

  const [gateway, setGateway] = useState<Gateway | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Notes editing
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  // Certificate
  const [showCertModal, setShowCertModal] = useState(false);
  const [certData, setCertData] = useState<GatewayCertificate | null>(null);
  const [certLoading, setCertLoading] = useState(false);
  const [confirmDeleteCert, setConfirmDeleteCert] = useState(false);

  // Devices transmitting through this gateway (last 7 days)
  const [devices, setDevices] = useState<GatewayDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);

  // Location edit dialog
  const [showLocationDialog, setShowLocationDialog] = useState(false);

  useEffect(() => {
    if (!id) return;
    setDevicesLoading(true);
    getGatewayDevices(id)
      .then(setDevices)
      .catch(() => setDevices([]))
      .finally(() => setDevicesLoading(false));
  }, [id]);

  // Background poll: visibility-gated detail-page cadence. Refreshes the
  // gateway metadata (lastSeenAt, status) and the connected-devices list
  // silently — no spinners between polls.
  usePagePolling(
    () => {
      if (!id) return;
      getGateway(id)
        .then((res) => { if (res.success && res.data) setGateway(res.data); })
        .catch(() => {});
      getGatewayDevices(id)
        .then(setDevices)
        .catch(() => {});
    },
    POLL_INTERVAL_DETAIL_MS,
    { enabled: !!id },
  );

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getGateway(id)
      .then((res) => {
        if (res.success && res.data) {
          setGateway(res.data);
          setNotes(res.data.notes ?? '');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSaveNotes() {
    if (!id || !gateway) return;
    setNotesSaving(true);
    try {
      await updateGatewayNotes(id, notes);
      setGateway({ ...gateway, notes });
      setNotesDirty(false);
    } catch {
      // silent
    } finally {
      setNotesSaving(false);
    }
  }

  async function handleDelete() {
    if (!id) return;
    setIsDeleting(true);
    try {
      await deleteGateway(id);
      navigate(-1);
    } catch {
      // silent
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function handleCreateCertificate() {
    if (!id) return;
    setCertLoading(true);
    setCertData(null);
    setShowCertModal(true);
    try {
      const response = await createGatewayCertificate(id);
      if (response.success && response.data) {
        setCertData(response.data);
        // Refresh gateway to show updated cert status
        const refreshed = await getGateway(id);
        if (refreshed.success && refreshed.data) setGateway(refreshed.data);
      }
    } catch {
      // silent
    } finally {
      setCertLoading(false);
    }
  }

  async function handleDownloadServerTrust() {
    try {
      const response = await getServerTrustCertificate();
      if (response.success && response.data) {
        downloadFile('cups.trust', response.data);
      }
    } catch {
      // silent
    }
  }

  async function handleDeleteCertificate() {
    if (!id || !gateway) return;
    setConfirmDeleteCert(false);
    try {
      await deleteGatewayCertificate(id);
      setGateway({ ...gateway, certificateGenerated: false, certificateId: undefined });
    } catch {
      // silent
    }
  }

  if (loading) return <LoadingPage />;

  if (!gateway) {
    return (
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <p className="text-gray-500">Gateway not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>Back</Button>
        </div>
      </div>
    );
  }

  const recent = gateway.lastSeenAt && (Date.now() - new Date(gateway.lastSeenAt).getTime()) < STALE_MS;
  const signalIcon =
    gateway.status === 'Error' ? <AlertTriangle className="w-5 h-5 text-error-500" /> :
    recent ? <Wifi className="w-5 h-5 text-success-500" /> :
    <WifiOff className="w-5 h-5 text-gray-400" />;

  return (
    <>
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Breadcrumb items={[
            { label: 'Gateways', href: '/admin/gateways' },
            { label: gateway.name },
          ]} />

          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {signalIcon}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{gateway.name}</h1>
                <p className="text-sm font-mono text-gray-500 dark:text-gray-400 mt-0.5">{gateway.gatewayEui}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isSystemAdmin && (gateway.certificateGenerated ? (
                <>
                  <Button variant="outline" onClick={handleCreateCertificate}>
                    <ShieldCheck className="w-4 h-4" />
                    Regenerate Certificate
                  </Button>
                  <Button variant="ghost" className="text-error-600 hover:text-error-700" onClick={() => setConfirmDeleteCert(true)}>
                    <Trash2 className="w-4 h-4" />
                    Delete Certificate
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={handleCreateCertificate}>
                  <ShieldCheck className="w-4 h-4" />
                  Create Certificate
                </Button>
              ))}
              {isSystemAdmin && (
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  <Trash2 className="w-4 h-4" />
                  Delete Gateway
                </Button>
              )}
            </div>
          </div>

          {/* Info Card */}
          <Card variant="elevated">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Status</p>
                <Badge variant={statusBadge[gateway.status]} size="sm" dot>{gateway.status}</Badge>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">RF Region</p>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{gateway.rfRegion}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Certificate</p>
                {gateway.certificateGenerated ? (
                  <Badge variant="success" size="sm">Generated</Badge>
                ) : (
                  <Badge variant="default" size="sm">Pending</Badge>
                )}
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Last Seen</p>
                <p className="text-gray-900 dark:text-gray-100 font-medium">
                  {gateway.lastSeenAt ? formatRelativeTime(gateway.lastSeenAt) : 'Never'}
                </p>
              </div>
              <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-gray-400 text-xs uppercase tracking-wide">Location</p>
                  {canEditLocation && (
                    <Button variant="ghost" size="sm" onClick={() => setShowLocationDialog(true)}>
                      <MapPin className="w-3.5 h-3.5" />
                      {gateway.latitude != null && gateway.longitude != null ? 'Edit' : 'Set location'}
                    </Button>
                  )}
                </div>
                {gateway.latitude != null && gateway.longitude != null ? (
                  <p className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                    {gateway.latitude.toFixed(7)}, {gateway.longitude.toFixed(7)}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Not set</p>
                )}
              </div>
              {gateway.description && (
                <div className="col-span-2">
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Description</p>
                  <p className="text-gray-900 dark:text-gray-100">{gateway.description}</p>
                </div>
              )}
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Created</p>
                <p className="text-gray-900 dark:text-gray-100 font-medium">{formatDate(gateway.createdAt)}</p>
              </div>
              {gateway.awsWirelessGatewayId && (
                <div className="col-span-2">
                  <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">AWS Gateway ID</p>
                  <p className="text-gray-500 dark:text-gray-400 font-mono text-xs">{gateway.awsWirelessGatewayId}</p>
                </div>
              )}
              <div className="col-span-2 sm:col-span-3 lg:col-span-4">
                <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">CUPS Server URL</p>
                <code className="text-xs text-gray-600 dark:text-gray-300 font-mono select-all">
                  https://A2YJ2ON53N7KDS.cups.lorawan.us-east-1.amazonaws.com:443
                </code>
              </div>
            </div>
          </Card>

          {/* Map — shows this gateway's location, when set. The `key` is
              bumped on coord change so the Google Maps instance remounts
              and re-honors `center`; the underlying API treats `center`
              as a hint after first render, so prop changes alone don't
              recenter the pin after a save. */}
          {gateway.latitude != null && gateway.longitude != null && (() => {
            const marker: MapMarker = {
              id: `gateway-${gateway.id}`,
              lat: gateway.latitude,
              lng: gateway.longitude,
              label: gateway.name,
              color: 'slate',
              icon: 'gateway',
            };
            return (
              <Card variant="elevated" padding="none">
                <GoogleMap
                  key={`${gateway.latitude},${gateway.longitude}`}
                  markers={[marker]}
                  center={{ lat: gateway.latitude, lng: gateway.longitude }}
                  zoom={17}
                  className="h-56"
                />
              </Card>
            );
          })()}

          {/* Devices transmitting through this gateway */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Connected Devices</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Last 7 days</p>
            </div>
            {devicesLoading ? (
              <Card variant="elevated">
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
              </Card>
            ) : devices.length === 0 ? (
              <Card variant="elevated">
                <EmptyState
                  icon={<Activity className="w-10 h-10" />}
                  title="No devices"
                  description="No devices have transmitted through this gateway in the last 7 days."
                />
              </Card>
            ) : (
              <Card variant="elevated" padding="none">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial</TableHead>
                      <TableHead>Device EUI</TableHead>
                      <TableHead>Property / Unit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Last Seen (via this gateway)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((d) => (
                      <TableRow
                        key={d.id}
                        className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        onClick={() => navigate(`/devices/${d.id}`)}
                      >
                        <TableCell>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{d.serialNumber}</span>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs text-gray-600 dark:text-gray-400 font-mono">{d.devEui}</code>
                        </TableCell>
                        <TableCell>
                          {d.propertyName ? (
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {d.propertyName}{d.unitNumber ? <span className="text-gray-400"> · {d.unitNumber}</span> : null}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Unassigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={d.status === DeviceStatus.Active ? 'success' : d.status === DeviceStatus.Error ? 'error' : 'default'}
                            size="sm"
                            dot
                          >
                            {d.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {formatRelativeTime(d.lastSeenThroughGateway)}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>

          {/* Notes */}
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Notes</h2>
            <Card variant="elevated">
              <textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
                placeholder="Add notes about this gateway..."
                rows={4}
                className="w-full bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none focus:outline-none"
              />
              {notesDirty && (
                <div className="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
                  <Button size="sm" onClick={handleSaveNotes} isLoading={notesSaving}>
                    Save Notes
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Location edit dialog */}
      {showLocationDialog && (
        <GatewayLocationDialog
          gateway={gateway}
          onClose={() => setShowLocationDialog(false)}
          onSaved={(updated) => {
            setGateway(updated);
            setShowLocationDialog(false);
          }}
        />
      )}

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={confirmDelete}
        title={`Delete gateway "${gateway.name}"?`}
        description={
          <>
            <p>This will permanently disconnect the gateway from the AWS IoT backend.</p>
            <p>If the gateway is re-added later, it may need to be reprovisioned before it can reconnect.</p>
          </>
        }
        confirmLabel="Delete Gateway"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
        isLoading={isDeleting}
      />

      <ConfirmDialog
        isOpen={confirmDeleteCert}
        title="Delete Certificate"
        description="Delete this gateway's certificate? The gateway will lose its CUPS connection until a new certificate is generated."
        confirmLabel="Delete Certificate"
        onConfirm={handleDeleteCertificate}
        onCancel={() => setConfirmDeleteCert(false)}
      />

      {/* Certificate Modal */}
      <Modal
        isOpen={showCertModal}
        onClose={() => { setShowCertModal(false); setCertData(null); }}
        title="Gateway CUPS Credentials"
        description="Download these files to configure your gateway's CUPS connection."
        size="lg"
      >
        {certLoading ? (
          <LoadingPage />
        ) : certData ? (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-warning-50 dark:bg-warning-500/20 border border-warning-200 dark:border-warning-500/30">
              <p className="text-sm text-warning-700 dark:text-warning-500 font-medium">
                Save the private key now — it cannot be retrieved again.
              </p>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Gateway Certificate</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">CUPS certificate for mutual TLS</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => downloadFile(`${certData.certificateId}-certificate.pem.crt`, certData.certificatePem)}>
                  <Download className="w-4 h-4" /> Download
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl border border-error-200 dark:border-error-500/30 bg-error-50/50 dark:bg-error-500/10">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Private Key</p>
                  <p className="text-xs text-error-600 dark:text-error-500">Only available now — keep secure</p>
                </div>
                <Button variant="danger" size="sm" onClick={() => downloadFile(`${certData.certificateId}-private.pem.key`, certData.privateKey)}>
                  <Download className="w-4 h-4" /> Download
                </Button>
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">CUPS Trust Certificate</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Starfield Root CA G2</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownloadServerTrust}>
                  <Download className="w-4 h-4" /> Download
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-error-600">Failed to create certificate.</p>
        )}
      </Modal>
    </>
  );
}
